import { NAVIGATION } from '../containers/App/constants';
import { isCrawler } from '../helpers';
import {
  ACCESS_TOKEN,
  API_URL,
  BASIC_REQUEST_HEADERS,
  HTTP,
  HTTP_STATUS_CODES,
  REFRESH_TOKEN,
  SIGNATURE_REJECTED_ERROR_KEY,
  TOKEN_EXPIRED_ERROR_KEY,
} from '../helpers/constants';
import { Sentry } from '../libs/sentry';
import history from '../utils/history';
import { disabledErrorKeys } from './constants';
import signRequest from './requestSignature';
import getSessionToken, { getClockOffset } from './sessionToken';

/**
 * Parses the JSON returned by a network request
 *
 * @param  {object} response A response from a network request
 *
 * @return {object}          The parsed JSON from the request
 */
function parseJSON(response) {
  if (response.status === HTTP.STATUS_CODES.RESET_CONTENT) {
    return null;
  }
  if (response.status === HTTP.STATUS_CODES.NO_CONTENT) {
    return HTTP.STATUS_CODES.NO_CONTENT;
  }
  return response.json();
}

/**
 * Checks if a network request came back fine, and throws an error if not
 *
 * @param {Response} response A response from a network request
 * @param {string} url The URL we want to request
 * @param {object} options The options we want to pass to "fetch"
 * @param {number} retry Number of retry
 * @param {number[]} [acceptedStatuses] Non-2xx statuses the caller treats as a payload, not an error
 * @return {Response|undefined} Returns either the response, or throws an error
 */
async function checkStatus(
  response,
  url,
  options,
  retry,
  acceptedStatuses = [],
) {
  if (
    (response.status >= 200 && response.status < 300) ||
    acceptedStatuses.includes(response.status)
  ) {
    return response;
  }

  if (response.status === HTTP_STATUS_CODES.BAD_GATEWAY) {
    await delay(1000);

    return requestRaw(url, options, retry, acceptedStatuses);
  }

  let error;
  try {
    const { errorKey, errorMessage, fields } = await response.json();

    if (errorKey && errorKey === TOKEN_EXPIRED_ERROR_KEY) {
      const res = await fetch(`${API_URL}/auth`, {
        method: HTTP.METHODS.PUT,
        headers: {
          ...BASIC_REQUEST_HEADERS,
          authorization: `Bearer ${localStorage.getItem(ACCESS_TOKEN)}`,
          refresh: `Refresh ${localStorage.getItem(REFRESH_TOKEN)}`,
        },
      });
      if (res.status === 200 && retry > 0) {
        const { refreshToken, token } = await res.json();
        localStorage.setItem(ACCESS_TOKEN, token);
        localStorage.setItem(REFRESH_TOKEN, refreshToken);

        return requestRaw(
          url,
          {
            ...options,
            headers: { ...options.headers, authorization: `Bearer ${token}` },
          },
          retry - 1,
          acceptedStatuses,
        );
      } else {
        history.push(NAVIGATION.LOGIN);
      }
    }

    error = await formatError(response, errorKey, errorMessage, fields);
  } catch (e) {
    error = new Error(e);
    error.response = response;
    error.capturedBySentry = true;
  }

  if (isPrerender(window) && isErrorIgnored(error)) {
    throw error;
  }

  Sentry.withScope((scope) => {
    scope.setExtra('status', response.status);
    scope.setExtra('url', response.url);
    scope.setExtra('type', response.type);
    scope.setExtra('fields', error.fields);
    Sentry.captureException(error);
  });

  throw error;
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * @description Formats error thrown using error key if it exists
 * @param {Response} response
 * @returns {Promise<Error>}
 */
const formatError = async (response, errorKey, errorMessage, fields) => {
  let error = new Error(`Unexpected error ${response.status}`);

  if (errorKey) {
    error = new Error(errorKey);
    error.errorKey = errorKey;
    error.errorMessage = errorMessage;
  }
  if (fields) {
    error.fields = fields;
  }

  error.response = response;
  error.capturedBySentry = true;

  return error;
};

/** Endpoints the API only serves to our own front, which must carry a signed session. */
const SESSION_PROTECTED_PATHS = [
  '/learn/user-searchs',
  '/trips/search-async',
  '/checkAvailability/trip',
  // The contact forms, which bots were spamming.
  '/support/ticket',
  '/support/v2/ticket',
];

/** Endpoints protected for their creation only: what the front reads from them right after needs no session. */
const SESSION_PROTECTED_POST_PATHS = [
  // Opens a Payline payment. Its offers and the status polled while paying stay unsigned.
  '/discount-card-purchases',
  // The same for products added to a paid order
  '/ancillary-purchases',
  // The same for gift cards, and for gift pots and their contributions
  '/gift-card-purchases',
  '/gift-pots',
  // Creates a cart and runs the carriers' price checks. Reading or filling it needs no session: a
  // visitor sent by Google or Kayak lands straight on the cart, before any challenge could finish.
  '/booking/carts',
];

/** A promo code tried on a cart, kept behind the session so codes cannot be tried in bulk. */
const CART_PROMO_CODES_PATH = /\/booking\/carts\/[^/]+\/promo-codes/;
/** The balance of a gift card, kept behind the session for the same reason. */
const GIFT_CARD_CODE_PATH = /\/gift-card-purchases\/codes\//;

/**
 * Crawlers we serve on purpose are let through by the API on their address alone, and could not
 * solve the challenge anyway. Putting them through a session only made them wait for it to fail:
 * Google Flights renders our result pages, and a search held back by a doomed challenge risked
 * being read as a page with no prices. The API still checks their address, so a scraper faking a
 * crawler's user-agent gains nothing here.
 */
const needsSession = (url, options) =>
  !isCrawler(navigator.userAgent) &&
  (SESSION_PROTECTED_PATHS.some((path) => String(url).includes(path)) ||
    CART_PROMO_CODES_PATH.test(String(url)) ||
    GIFT_CARD_CODE_PATH.test(String(url)) ||
    (options?.method === 'POST' &&
      SESSION_PROTECTED_POST_PATHS.some((path) => String(url).includes(path))));

/**
 * @description Adds the session token and the per-request signature the API expects on its
 * front-only endpoints. The signature covers the nonce, the timestamp, the method, the path and a
 * hash of the body, so a captured request can neither be replayed nor edited.
 *
 * Only ever awaited behind `needsSession`: every other endpoint has to reach `fetch` in its
 * caller's own tick, as it did before sessions existed. Awaiting unconditionally would push each
 * of them a microtask later, which callers observe.
 *
 * @param {string} url The URL we want to request
 * @param {object} [options] The options we want to pass to "fetch"
 * @param {boolean} [renew] Discard the stored token and mint a new one first
 * @return {Promise<object>} The options, carrying the session headers
 */
async function signSession(url, options, renew = false) {
  const token = await getSessionToken(renew);

  if (!token) {
    return options;
  }

  const signature = await signRequest(token, url, options, getClockOffset());

  return {
    ...options,
    headers: {
      ...(options?.headers || {}),
      'X-Ttt-Session': token,
      ...(signature || {}),
    },
  };
}

/**
 * @description Reads the error key of a refusal without consuming the response the caller still
 * has to read.
 * @param {Response} response
 * @returns {Promise<string|null>}
 */
const readErrorKey = async (response) => {
  try {
    const { errorKey } = await response.clone().json();

    return errorKey || null;
  } catch (error) {
    return null;
  }
};

/**
 * @description Signs the call again after the API refused it, and says whether a new session is
 * needed to do so.
 *
 * Minting is the expensive path: it costs the visitor a Turnstile challenge and rotates the key the
 * API's daily limiter counts on, so it is reserved for a session the API will not accept. A refusal
 * that names the signature leaves the session intact - the call only lacked a fresh nonce, which
 * happens whenever the ingress replays a request whose first attempt already spent one.
 *
 * @param {Response} response The refusal
 * @param {string} url The URL we want to request
 * @param {object} [options] The options we want to pass to "fetch"
 * @returns {Promise<object>} The options, signed again
 */
const signAfterRefusal = async (response, url, options) => {
  const errorKey = await readErrorKey(response);

  return signSession(url, options, errorKey !== SIGNATURE_REJECTED_ERROR_KEY);
};

/**
 * @description Requests a URL, returning a promise
 * @param  {string} url - The URL we want to request
 * @param  {object} [options] - The options we want to pass to "fetch"*
 * @param {number} retry Number of retry
 * @param {number[]} [acceptedStatuses] - Non-2xx statuses returned as a
 * regular response instead of being thrown (and reported to Sentry). Opt-in,
 * for endpoints whose non-2xx body is a usable answer (e.g. a 422 partial
 * extraction). Every other status keeps the standard error handling.
 * @return {Response} - The response
 */
export async function requestRaw(
  url,
  options,
  retry = 1,
  acceptedStatuses = [],
) {
  const signed = needsSession(url, options)
    ? await signSession(url, options)
    : options;

  return fetch(url, signed).then((res) =>
    checkStatus(res, url, signed, retry, acceptedStatuses),
  );
}

/**
 * Requests a URL, returning a promise
 *
 * @param  {string} url       The URL we want to request
 * @param  {object} [options] The options we want to pass to "fetch"
 * @param {number} retry Number of retry
 * @return {object}           The response data
 */
export default async function request(url, options, retry = 1) {
  try {
    const signed = needsSession(url, options)
      ? await signSession(url, options)
      : options;
    const response = await fetch(url, signed)
      .then(async (res) => {
        // The API refused the call: sign it again - minting a session only when it says the
        // session itself is the problem - and replay once.
        if (
          res.status === HTTP.STATUS_CODES.UNAUTHORIZED &&
          needsSession(url, options)
        ) {
          const renewed = await signAfterRefusal(res, url, options);

          return fetch(url, renewed).then((retried) =>
            checkStatus(retried, url, renewed, retry),
          );
        }

        return checkStatus(res, url, signed, retry);
      })
      .then(parseJSON);

    return response;
  } catch (error) {
    if (error?.message === 'Failed to fetch') {
      await delay(1000);
      return request(url, options, retry);
    }

    throw error;
  }
}

export const isPrerender = ({ navigator }) =>
  // We are relying on PreRender's documentation
  // https://docs.prerender.io/article/7-faq
  /^.*Prerender \(\+https:\/\/github\.com\/prerender\/prerender\).*$/.test(
    navigator.userAgent,
  );

export const isErrorIgnored = ({ errorKey }) =>
  !!errorKey && disabledErrorKeys.includes(errorKey);
